<template>
    <CompassModal
        v-model:visible="showModal"
        size="small"
        :title="title"
        class="esign-popup-provider"
        :closeOnClickOverlay="false"
        @closed="$emit('close')"
    >
        <template #default>
            <div class="dialog__default">
                <div v-if="step === 1">
                    <el-radio-group
                        v-model="tempProviderSelected.id"
                        class="flex-column"
                        data-test="providers-radio-group"
                        @change="onProviderSelected"
                    >
                        <el-radio
                            v-for="(provider, key) in providers"
                            :key="provider.id"
                            :ref="provider.id"
                            :class="key !== providers.length - 1 ? 'mb-4' : ''"
                            :label="provider.id"
                            class="flex-row ai-center"
                            data-test="provider-option"
                        >
                            <span class="flex-row ai-center">
                                <SaasIcon
                                    v-if="providerSelected.name"
                                    :icon="provider.name === 'Sellsy' ? 'sellsyCloud' : 'docusign'"
                                    class="provider-icon ml-3"
                                    height="40"
                                    width="40"
                                />
                                <span class="semibold fs-3 ml-2 gray-100 lh-20">
                                    {{ provider.name }}
                                </span>
                                <CompassTag v-if="provider.id === providersIds.sellsy" size="small">
                                    {{ $t.__('common.labels.by_default') }}
                                </CompassTag>
                            </span>
                        </el-radio>
                    </el-radio-group>
                </div>
                <div v-if="step === 2" ref="contentStep2">
                    <div v-if="isConnected" class="alert alert-warning">
                        {{
                            $l10n.__(
                                'Vous vous apprêtez à changer de service. En déconnectant votre compte Docusign, le service de signature électronique Sellsy sera réactivé par défaut. Attention, si vous avez des signatures en cours d’envoi, elles ne pourront pas être finalisées par vos destinataires.',
                            )
                        }}
                    </div>
                    <div v-else class="alert alert-warning">
                        {{
                            $l10n.__(
                                'Vous vous apprêtez à changer de service. Toutes les prochaines signatures électronique seront désormais envoyées par Docusign. Vous pouvez toujours revenir à l’ancien service.',
                            )
                        }}
                    </div>
                </div>
            </div>
            <preloader :display="loading" />
        </template>
        <template #footer>
            <div class="dialog__footer">
                <CompassButton
                    :label="$t.__('common.actions.cancel')"
                    type="secondary"
                    size="medium"
                    @click="close"
                />
                <CompassButton
                    ref="continueBtn"
                    :label="submitBtnText"
                    type="primary"
                    size="medium"
                    :disabled="submitBtnDisabled"
                    class="ml-3"
                    @click="onClickContinue"
                />
            </div>
        </template>
    </CompassModal>
</template>

<script>
import TagRounded from 'vcomponents/utilities/TagRounded.vue';
import Preloader from 'vcomponents/utilities/Preloader.vue';
import { createNamespacedHelpers } from 'vuex';
import _ from 'lodash';
import { providersIds } from 'stores/esignStore';
import { goToUrl } from '../../helpers';
import SaasIcon from 'vcomponents/common/SaasIcon.vue';

const esign = createNamespacedHelpers('esign');
export const modeEnum = {
    CHANGE_PROVIDER: 'changeProvider',
    LOGOUT_PROVIDER: 'logoutProvider',
    LOGIN_PROVIDER: 'loginProvider',
};

export default {
    components: {
        SaasIcon,
        Preloader,
        TagRounded,
    },
    props: {
        mode: {
            type: String,
            required: false,
            default: modeEnum.CHANGE_PROVIDER,
        },
    },
    data() {
        return {
            showModal: true,
            step: 1,
            loading: true,
            providersIds: providersIds,
            tempProviderSelected: {},
        };
    },
    computed: {
        ...esign.mapGetters(['isConnected', 'providers', 'isProviderSellsy', 'providerSelected']),
        title() {
            if (this.step === 1) {
                return this.$l10n.__('Modifier le service de signature électronique');
            } else {
                return this.isConnected
                    ? this.$l10n.__('Déconnexion du service Docusign')
                    : this.$l10n.__('Connexion au service Docusign');
            }
        },
        submitBtnText() {
            if (this.step === 1 && this.tempProviderSelected.id === this.providersIds.sellsy) {
                return this.$l10n.__('Confirmer');
            }
            return this.step === 1 ? this.$l10n.__('Continuer') : this.$l10n.__('Confirmer');
        },
        submitBtnDisabled() {
            if (!this.isProviderSellsy) {
                return false;
            }
            if (
                this.mode === modeEnum.CHANGE_PROVIDER &&
                this.tempProviderSelected.id === this.providersIds.sellsy &&
                !this.isConnected
            ) {
                return true;
            }
            return false;
        },
    },
    created() {
        switch (this.mode) {
            case modeEnum.CHANGE_PROVIDER:
                this.step = 1;
                break;
            case modeEnum.LOGIN_PROVIDER:
                this.step = 2;
                break;
            case modeEnum.LOGOUT_PROVIDER:
                this.step = 2;
                break;
        }
        this.tempProviderSelected = Object.assign({}, this.providerSelected);
        this.loading = false;
    },
    methods: {
        ...esign.mapActions([
            'setDocusignSelected',
            'updateProviderSelected',
            'confirmDocusignSelected',
            'checkIsProviderSellsy',
            'saveProvider',
        ]),
        onClickContinue() {
            if (this.step === 1) {
                if (this.tempProviderSelected.id === providersIds.sellsy) {
                    this.loading = true;
                    this.saveProvider(this.providersIds.sellsy).finally(() => {
                        this.loading = false;
                        this.close();
                    });
                } else {
                    this.step++;
                }
            } else {
                this.loading = true;
                if (this.isConnected) {
                    this.saveProvider(this.providersIds.sellsy).finally(() => {
                        this.loading = false;
                        this.close();
                    });
                } else {
                    this.saveProvider(this.tempProviderSelected.id)
                        .then(() => {
                            this.tempProviderSelected = Object.assign({}, this.providerSelected);
                            this.redirectToProviderSignin();
                        })
                        .catch(() => {
                            this.loading = false;
                            Push.error(
                                this.$l10n.__('Une erreur est survenue lors de la validation'),
                            );
                        });
                }
            }
        },
        close() {
            this.$emit('close');
        },
        onProviderSelected(id) {
            this.tempProviderSelected = Object.assign(
                {},
                _.find(this.providers, (provider) => provider.id === id),
            );
        },
        redirectToProviderSignin() {
            const url = new URL(window.location.href);
            goToUrl(`${url.protocol}//${url.host}${this.tempProviderSelected.oauth_url}`);
        },
    },
};
</script>

<style lang="scss">
.esign-popup-provider {
    .dialog {
        &__default {
            padding-bottom: 24px;
        }

        &__footer {
            display: flex;
            justify-content: flex-end;
            padding: 0 32px 24px 32px;
        }
    }

    .flex-row.ai-center {
        div .default-tag {
            border: none !important;
        }
    }

    .compass-tag {
        margin-left: var(--spacing-1);
    }
}
</style>
